/**
 * Handles the filters and templates for the content loader
 * 
 * @exports
 * @class PostsViewer
 */
import * as Flickity from 'flickity';

import { FadeSlideUp } from '../../animations/fade-slide-up';
import { ApplicationBlock } from '../../blocks/application-block';
import { get, getAll } from '../../helpers/utilities';
import { ContentLoader } from '../../modules/content-loader';

import { applicationBlock } from './templates';

export class ApplicationsViewer {
    private root: Element;
    private uiElements: PostsUI;
    private animationInstance!: FadeSlideUp;


    /**
     * Initialises a new instance of MaterialsViewer
     */
    constructor() {
        this.root = get('#js-applications') as Element;
        
        // Store our UI elements
        this.uiElements = {
            loadMore: get('#js-applications__load-more') as Element,
            disableElements: getAll('.js-applications__ui-elem') as Element[],
        }

        // Get the animation instance for our elements
        this.animationInstance = FadeSlideUp.get_instance(this.root);

        // Initialise our content viewer
        this.initLoader();
    }


    /**
     * Initialises our content loader
     * 
     * @private
     * @returns void
     */
    private initLoader(): void {
        const grid  = get('#js-applications__grid');
        if(this.root && grid) {

            new ContentLoader(this.root, {
                renderer: (data: ContentLoaderJSONResponse) => this.materialRenderer(data as ApplicationsJSONResponse),
                toggleUI: (shouldDisable: boolean) => { this.toggleUI(shouldDisable) },
                itemSelector: '.js-applications-item',
                events: {
                    afterInsert: (elements: Element[]) => { this.displayNewMaterials(elements); }
                },
                insertOptions: {
                    type: 'append',
                    element: grid
                },
            });
        }
    }


    /**
     * Renders the materials and passes then back to the content loader
     * 
     * @param data The JSON data object.
     * @param loader The loader instance.
     * @returns An array of HTML strings
     */
    private materialRenderer(data: ApplicationsJSONResponse): string[] {
        const materialStrings: string[] = [];

        data.items.forEach(material => {
            materialStrings.push(applicationBlock(material));
        });

        return materialStrings;
    }


    /**
     * Toggle the UI disabled or enabled
     * 
     * @private
     * @returns void
     */
    private toggleUI(shouldDisable: boolean): void {

        if(shouldDisable) {
            this.uiElements.disableElements.forEach(uiElement => {
                uiElement.classList.add('c-applications__ui--is-disabled');
            });
        } else {
            this.uiElements.disableElements.forEach(uiElement => {
                uiElement.classList.remove('c-applications__ui--is-disabled');
            });
        }
    }


    /**
     * Displays our new elements
     * 
     * @param elements The elements to display
     */
    private displayNewMaterials(elements: Element[]): void {

        // Init the gallery
        elements.forEach(element => {
            const gallery = get('.js-application-block__gallery', element);
            if(gallery) {
                const options = gallery.getAttribute('data-flickity') || '{}';
                new Flickity(gallery, JSON.parse(options));
                new ApplicationBlock(element);
            }
        });
        
        // Add the new animation items
        this.animationInstance.add(elements);
    }
}
